/* PKCE (RFC 7636) and the one-time values that bind an authorization round-trip.

   `crypto.subtle` exists ONLY in a secure context — https://, or http:// on
   localhost, which the spec treats as potentially-trustworthy. That is the real
   reason the dev callback is http://localhost:5173/callback and not
   http://192.168.x.x:5173/callback: on a LAN IP `crypto.subtle` is `undefined`
   and S256 cannot be computed at all. `crypto.getRandomValues` is available
   everywhere, so the failure shows up only at the digest, which makes it look
   like a bug in the flow rather than a property of the origin.

   Nothing here is Milo-specific; it is the platform doing the work. No library. */

/* RFC 7636 §4.1: the code_verifier alphabet is the unreserved characters
   [A-Z] / [a-z] / [0-9] / "-" / "." / "_" / "~", length 43–128. */
const UNRESERVED = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'

/** 64 characters ≈ 380 bits of entropy — comfortably inside the 43–128 window. */
const VERIFIER_LENGTH = 64

/** 32 characters for `state` and `nonce`; they only need to be unguessable. */
const TOKEN_LENGTH = 32

/**
 * A random string over the RFC 7636 unreserved alphabet.
 *
 * Uses rejection sampling rather than `byte % 66`. 256 is not a multiple of 66,
 * so the naive modulo would make the first 58 characters of the alphabet about
 * 1.5% likelier than the last 8. The bias is small, but it is free to avoid and
 * a biased verifier is a weaker verifier.
 */
function randomString(length: number): string {
  const limit = 256 - (256 % UNRESERVED.length)
  const out: string[] = []
  const buf = new Uint8Array(length)
  while (out.length < length) {
    crypto.getRandomValues(buf)
    for (const byte of buf) {
      if (byte >= limit) continue
      out.push(UNRESERVED[byte % UNRESERVED.length])
      if (out.length === length) break
    }
  }
  return out.join('')
}

/** base64url with no padding, per RFC 7636 §A. */
export function base64UrlEncode(input: ArrayBuffer | Uint8Array): string {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** A fresh `code_verifier`. Keep it secret; it is the proof the redirect came back to us. */
export function createCodeVerifier(): string {
  return randomString(VERIFIER_LENGTH)
}

/** A fresh `state` or `nonce`. */
export function createOpaqueToken(): string {
  return randomString(TOKEN_LENGTH)
}

/**
 * The S256 `code_challenge` for a verifier.
 *
 * We send `code_challenge_method=S256` explicitly and always. Cardea hardcodes
 * `EnablePKCEPlainChallengeMethod: true` with no override and advertises
 * `plain` in `code_challenge_methods_supported`, so it would accept a plaintext
 * challenge — which OAuth 2.1 forbids, because a `plain` challenge is worth
 * nothing against an attacker who can see the authorization request. The server
 * will not enforce S256 for us; this function is the enforcement.
 */
export async function createCodeChallenge(verifier: string): Promise<string> {
  if (!crypto.subtle) {
    throw new Error(
      'crypto.subtle is unavailable, so the PKCE challenge cannot be computed. ' +
        'This page must be served from https:// or from http://localhost — a LAN ' +
        'IP over http is not a secure context.',
    )
  }
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64UrlEncode(digest)
}
